import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, ClipboardCheck, Calendar, AlertCircle, Clock, CheckCircle, FileText, Video, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { InspectionService } from '../services/inspectionService';
import { LoadingSpinner } from '../components';
import { Inspection } from '../types';
import { CreateInspectionModal } from '../components/Inspections/CreateInspectionModal';

export default function InspectionReports() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'draft' | 'processing' | 'completed'>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const inspectionService = InspectionService.getInstance();

  useEffect(() => {
    loadInspections();
  }, [firebaseUser]);

  const loadInspections = async () => {
    if (!firebaseUser) return;

    try {
      setLoading(true);
      setError('');
      
      console.log('📋 Loading user inspections...');
      const userInspections = await inspectionService.getUserInspections(firebaseUser.uid);
      
      console.log('✅ Loaded inspections:', userInspections.length);
      setInspections(userInspections);
    } catch (err: any) {
      console.error('❌ Error loading inspections:', err);
      setError(`Failed to load inspections: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesSearch = inspection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inspection.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || inspection.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: Inspection['status']) => {
    switch (status) {
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: Inspection['status']) => {
    switch (status) {
      case 'draft': return <Clock className="w-4 h-4" />;
      case 'processing': return <FileText className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const handleInspectionClick = (inspection: Inspection) => {
    navigate(`/inspections/${inspection.id}`);
  };

  const handleCreateInspection = () => {
    setIsCreateModalOpen(true);
  };

  const handleInspectionCreated = (newInspection: Inspection) => {
    setInspections(prevInspections => [newInspection, ...prevInspections]);
    console.log('Inspection created and added to list:', newInspection);
    // Navigate to the editor
    navigate(`/inspections/${newInspection.id}`);
  };

  const handleDeleteInspection = async (e: React.MouseEvent, inspectionId: string) => {
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this inspection? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingId(inspectionId);
      await inspectionService.deleteInspection(inspectionId);
      setInspections(prevInspections => 
        prevInspections.filter(inspection => inspection.id !== inspectionId)
      );
      console.log('✅ Inspection deleted:', inspectionId);
    } catch (err: any) {
      console.error('❌ Error deleting inspection:', err);
      setError(`Failed to delete inspection: ${err.message || 'Unknown error'}`);
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <LoadingSpinner message="Loading inspections..." color="orange" fullPage />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <ClipboardCheck className="h-8 w-8 text-orange-600" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Inspection Reports</h1>
                <p className="text-lg text-gray-600 mt-1">
                  Create AI-powered inspection reports from video walkthroughs
                </p>
              </div>
            </div>
            <button
              onClick={handleCreateInspection}
              className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Inspection
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search inspections..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="all">All Inspections</option>
              <option value="draft">Draft</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <span className="text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Inspections Grid */}
        {filteredInspections.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardCheck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filterStatus !== 'all' ? 'No matching inspections' : 'No inspections yet'}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchTerm || filterStatus !== 'all' 
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first inspection report to get started'
              }
            </p>
            {!searchTerm && filterStatus === 'all' && (
              <button
                onClick={handleCreateInspection}
                className="inline-flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Plus className="w-5 h-5 mr-2" />
                Create First Inspection
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInspections.map((inspection) => (
              <div
                key={inspection.id}
                onClick={() => handleInspectionClick(inspection)}
                className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div className="p-6">
                  {/* Inspection Header */}
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">
                      {inspection.name}
                    </h3>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(inspection.status)}`}>
                      {getStatusIcon(inspection.status)}
                      <span className="ml-1 capitalize">{inspection.status}</span>
                    </span>
                  </div>

                  {/* Inspection Description */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {inspection.description || 'No description provided'}
                  </p>

                  {/* Video Info */}
                  {inspection.videoName && (
                    <div className="flex items-center text-sm text-orange-600 mb-4">
                      <Video className="w-4 h-4 mr-2" />
                      <span className="truncate">{inspection.videoName}</span>
                    </div>
                  )}

                  {/* Drive Folder Link */}
                  {inspection.driveFolder && (
                    <div className="flex items-center text-sm text-blue-600 mb-4">
                      <FileText className="w-4 h-4 mr-2" />
                      <span className="truncate">{inspection.driveFolder.name}</span>
                    </div>
                  )}

                  {/* Inspection Metadata */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>Updated {new Date(inspection.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center">
                      {inspection.reportDocUrl ? (
                        <a
                          href={inspection.reportDocUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          View Report
                        </a>
                      ) : (
                        <span className="text-gray-400 text-sm">No report yet</span>
                      )}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={(e) => handleDeleteInspection(e, inspection.id)}
                          disabled={deletingId === inspection.id}
                          className="text-red-600 hover:text-red-700 p-1 rounded disabled:opacity-50"
                          title="Delete inspection"
                        >
                          {deletingId === inspection.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                        <button className="text-orange-600 hover:text-orange-700 text-sm font-medium">
                          Open
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-yellow-600 mb-2">
              {inspections.filter(i => i.status === 'draft').length}
            </div>
            <div className="text-sm text-gray-600">Draft</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-blue-600 mb-2">
              {inspections.filter(i => i.status === 'processing').length}
            </div>
            <div className="text-sm text-gray-600">Processing</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-green-600 mb-2">
              {inspections.filter(i => i.status === 'completed').length}
            </div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
            <div className="text-2xl font-bold text-orange-600 mb-2">
              {inspections.length}
            </div>
            <div className="text-sm text-gray-600">Total Inspections</div>
          </div>
        </div>
      </main>

      {/* Create Inspection Modal */}
      <CreateInspectionModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onInspectionCreated={handleInspectionCreated}
      />
    </div>
  );
}
